import { useEffect, useState } from 'react'

// What version customers are actually on.
//
// Support reads tickets on the dashboard beside this, and "which build is
// this?" is the first question on most of them. A hard-coded constant would
// answer it wrongly the first time someone forgot to bump it — the dashboard
// would state a version with complete confidence and be a release behind. So
// it is read from the public releases feed and never stored.
//
// If the fetch fails the hook returns null and the dashboard shows nothing
// rather than a stale or guessed version: an honest gap beats a confident
// wrong answer, and nothing else on the page depends on it.

const FEED = 'https://api.github.com/repos/saasmouth/focusbuddy/releases/latest'

export interface LatestRelease {
  version: string
  publishedAt: string
  url: string
}

export function useLatestRelease(): LatestRelease | null {
  const [release, setRelease] = useState<LatestRelease | null>(null)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    // A dashboard tile must never hold the page up; give it a few seconds.
    const timer = setTimeout(() => controller.abort(), 6000)

    fetch(FEED, { signal: controller.signal, headers: { accept: 'application/vnd.github+json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { tag_name?: string; published_at?: string; html_url?: string } | null) => {
        if (cancelled || !j?.tag_name) return
        setRelease({
          version: j.tag_name.replace(/^v/, ''),
          publishedAt: j.published_at ?? '',
          url: j.html_url ?? ''
        })
      })
      .catch(() => {
        /* offline, rate-limited, or the feed moved — show nothing. */
      })
      .finally(() => clearTimeout(timer))

    return () => {
      cancelled = true
      controller.abort()
      clearTimeout(timer)
    }
  }, [])

  return release
}
